import { fetchConfig } from '~/global';
import { publish } from 'lib/pubsub';
import { PUB_SUB_EVENTS } from 'lib/constants';
import { getSecureRandomInt } from '~/utils';
import { formatDate, getEasternDate } from '~/utils';
import { checkForFee, isPhysicalItem } from '~/utils/fee-utils';
import { defineElement } from '~/utils/gw-utils';
import { getCartObject, getCartSectionsToRender } from '~/utils/cart-utils';

class ProductForm extends HTMLElement {
  constructor() {
    super();

    this.form = this.querySelector('form');
    this.variantIdInput.disabled = false;
    // this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
    this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

    this.standardSubmitButton = this.querySelector('.product-form__submit');
    this.standardSubmitButtonText = this.standardSubmitButton.querySelector('span');

    this.egiftSubmitButton = this.querySelector('.product-form__egift-submit');
    this.egiftSubmitButtonText = this.egiftSubmitButton.querySelector('span');

    if (document.querySelector('cart-drawer')) {
      this.standardSubmitButton.setAttribute('aria-haspopup', 'dialog');
      this.egiftSubmitButton.setAttribute('aria-haspopup', 'dialog');
    }

    this.standardSubmitButton.addEventListener('click', this.onSubmitHandler.bind(this));
    this.egiftSubmitButton.addEventListener('click', this.onSubmitHandler.bind(this));

    document.querySelector('[data-send-as-an-egift-popup]')?.addEventListener('click', () => {
      this.egiftSubmitButton.click();
    });

    this.hideErrors = this.dataset.hideErrors === 'true';
  }

  async onSubmitHandler(evt) {
    const formData = new FormData(this.form);
    const currentTarget = evt.currentTarget;

    // Age Customization option
    if (formData.has('properties[Recipient Age]')) {
      const recipientAge = formData.get('properties[Recipient Age]');
      const validationResult = await this.validateRecipientAge(recipientAge);
      if (validationResult === 'cancel') {
        return;
      }
    }

    // Initials Customization option
    if (formData.has('properties[Initials1]') && formData.has('properties[Initials2]')) {
      const initials1 = formData.get('properties[Initials1]');
      const initials2 = formData.get('properties[Initials2]');

      formData.append('properties[Initials]', `${initials1},${initials2}`);
    }

    const isEgift = currentTarget === this.egiftSubmitButton;
    const isCorporateGift = formData.get('properties[_corporate-gift]') === 'true';

    if (isEgift) {
      if (this.egiftSubmitButton.getAttribute('aria-disabled') === 'true') return;
    } else {
      if (this.standardSubmitButton.getAttribute('aria-disabled') === 'true') return;
    }

    this.handleErrorMessage();

    if (isEgift) {
      this.egiftSubmitButton.setAttribute('aria-disabled', true);
      this.egiftSubmitButton.classList.add('loading');
      this.egiftSubmitButton.querySelector('.loading__spinner').classList.remove('hidden');

      document
        .querySelector('[data-send-as-an-egift-popup]')
        ?.querySelector('.loading__spinner')
        ?.classList.remove('hidden');
    } else {
      this.standardSubmitButton.setAttribute('aria-disabled', true);
      this.standardSubmitButton.classList.add('loading');
      this.standardSubmitButton.querySelector('.loading__spinner').classList.remove('hidden');
    }

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const freshboardVariantId = formData.get('properties[_board-variant-id]');
    const egiftVariantId = formData.get('properties[_egift-variant-id]');
    const quantity = formData.get('quantity');

    // Because every quantity should be a separate item in the cart
    // We need to remove the quantity from the formData along with the id
    formData.delete('id');
    formData.delete('quantity');

    if (this.cart) {
      formData.append(
        'sections',
        getCartSectionsToRender().map((section) => section.section)
      );
      formData.append('sections_url', window.location.pathname);
      this.cart.setActiveElement(document.activeElement);
    }

    const propertiesToTransfer = [
      '_type',
      '_product-id',
      '_isFee',
      '_board-variant-id',
      '_egift-variant-id',
      '_corporate-gift',
      'Recipient Age',
      'Initials1',
      'Initials2',
      'Initials',
    ];

    const items = [];
    const uniqueGroupKey = getSecureRandomInt();

    for (let i = 1; i <= quantity; i++) {
      const item = {
        id: isEgift ? egiftVariantId : freshboardVariantId,
        quantity: 1,
        properties: {
          _groupKey: getSecureRandomInt(),
        },
      };

      if (isCorporateGift) {
        item.properties._corporateGroupKey = uniqueGroupKey;
      }

      propertiesToTransfer.forEach((property) => {
        const value = formData.get('properties[' + property + ']');
        if (value) {
          item.properties[property] = value;
        }
      });

      if (isEgift) {
        // Get today's date
        const todayInEST = getEasternDate();
        const formattedToday = formatDate({
          date: todayInEST.date,
          separator: '/',
        });

        item.properties['_e-gift'] = 'true';
        item.properties['_Ship By'] = formattedToday;
        item.properties['Arrival Date'] = formattedToday;
      } else {
        /**
              Add the arrival date and shipping date to the form data
              next-delivery-date is a custom element that calculates the next delivery date
              and holds the arrival date and shipping date as properties.
              Basically, we need to check if the next-delivery-date element is inside the product-form
              because on the card-product snippet, the next-delivery-date element is not a global element
              but on PDP next-delivery-date is a global element and outside of the product-form
            */
        let nextDeliveryDate = this.querySelector('next-delivery-date') || document.querySelector('next-delivery-date');

        if (!nextDeliveryDate) return;
        const { arrivalDate, arrivalDateISO, shippingDate } = nextDeliveryDate;

        item.properties['Arrival Date'] = arrivalDate;
        item.properties['_arrival-date-in-iso'] = arrivalDateISO;
        item.properties['_Ship By'] = shippingDate;

        // If the next delivery date is a fee day, add a fee product to the cart form data
        const arrivalDateObj = new Date(arrivalDateISO);
        const matchingFees = checkForFee(arrivalDateObj);

        if (matchingFees.length) {
          // Add the fee products data to the main product
          item.properties['_fee-product-added'] = true;
          item.properties['_fee-products-ids'] = matchingFees.map((fee) => fee.product.variants[0].id).join(',');

          matchingFees.forEach((fee, feeIndex) => {
            // Add the fee product as a separate line item
            items.push({
              id: fee.product.variants[0].id,
              quantity: 1,
              properties: {
                _connected: 'true',
                _groupKey: item.properties._groupKey,
                _corporateGroupKey: item.properties._corporateGroupKey,
                _handle: fee.product.handle,
                _transfer: 'true',
                _type: 'fee',
              },
            });
          });
        }
      }

      items.push(item);
    }

    // Remove the properties that were transfered to the line items
    propertiesToTransfer.forEach((property) => {
      formData.delete('properties[' + property + ']');
    });

    // Add the items to the form data
    items.forEach((item, i) => {
      formData.append('items[' + i + '][id]', item.id);
      formData.append('items[' + i + '][quantity]', item.quantity);

      Object.keys(item.properties).forEach((property) => {
        formData.append('items[' + i + '][properties][' + property + ']', item.properties[property]);
      });
    });

    if (!isEgift) {
      const cartObject = getCartObject(this);
      const totalPhysicalBoards = cartObject.totalPhysicalBoards + items.length;

      formData.append('attributes[physicalItemsCount]', totalPhysicalBoards);
    }

    config.body = formData;

    fetch(`${routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'product-form',
            productVariantId: formData.get('id'),
            errors: response.errors || response.description,
            message: response.message,
          });
          this.handleErrorMessage(response.description);

          if (isEgift) {
            const soldOutMessage = this.egiftSubmitButton.querySelector('.sold-out-message');
            if (!soldOutMessage) return;
            this.egiftSubmitButton.setAttribute('aria-disabled', true);
            this.egiftSubmitButtonText.classList.add('hidden');
            soldOutMessage.classList.remove('hidden');
          } else {
            const soldOutMessage = this.standardSubmitButton.querySelector('.sold-out-message');
            if (!soldOutMessage) return;
            this.standardSubmitButton.setAttribute('aria-disabled', true);
            this.standardSubmitButtonText.classList.add('hidden');
            soldOutMessage.classList.remove('hidden');
          }

          this.error = true;
          return;
        }

        // Dispatch saved cart item event for customer accounts
        const gwCustomerAccounts = document.querySelector('gw-customer-accounts');
        if (gwCustomerAccounts) {
          const event = new CustomEvent('gw:saved-items-from-cart', {
            detail: {
              productId: this.form.querySelector('[name="product-id"]').value,
              variantId: this.form.querySelector('[name="id"]').value,
              target: this.form,
              extra: response.items[0],
            },
          });

          gwCustomerAccounts.dispatchEvent(event);
        }

        // <------ Klaviyo tracking starts
        if (typeof item === 'undefined') {
          const addedItem = response.items[0];

          var item = {
            Name: addedItem.title,
            ProductID: addedItem.product_id,
            ImageURL: addedItem.image,
            URL: window.location.host + addedItem.url,
            Brand: addedItem.vendor,
            Price: Shopify.formatMoney(addedItem.price),
            Value: addedItem.price / 100,
            CompareAtPrice: '$0.00',
          };

          if (addedItem.compare_at_price) {
            item['CompareAtPrice'] = Shopify.formatMoney(addedItem.compare_at_price);
          }
        }

        document.documentElement.dispatchEvent(
          new CustomEvent('product:added', {
            bubbles: true,
            detail: {
              variant: item.id,
              quantity: 1,
              item: item,
            },
          })
        );
        // <------ Klaviyo tracking ends

        if (!this.cart) {
          window.location = window.routes.cart_url;
          return;
        }

        if (!this.error) {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: formData.get('id'),
            cartData: response,
          });
        }

        this.error = false;

        this.cart.renderContents(response);
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        if (isEgift) {
          this.egiftSubmitButton.classList.remove('loading');
        } else {
          this.standardSubmitButton.classList.remove('loading');
        }

        if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');

        if (!this.error) {
          if (isEgift) {
            this.egiftSubmitButton.removeAttribute('aria-disabled');
          } else {
            this.standardSubmitButton.removeAttribute('aria-disabled');
          }
        }

        this.querySelectorAll('.loading__spinner').forEach((spinner) => {
          spinner.classList.add('hidden');
        });

        document
          .querySelector('[data-send-as-an-egift-popup]')
          ?.querySelector('.loading__spinner')
          ?.classList.add('hidden');
      });
  }

  handleErrorMessage(errorMessage = false) {
    if (this.hideErrors) return;

    this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
    if (!this.errorMessageWrapper) return;
    this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

    this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

    if (errorMessage) {
      this.errorMessage.textContent = errorMessage;
    }
  }

  toggleSubmitButton(disable = true, text) {
    if (disable) {
      this.egiftSubmitButton.setAttribute('disabled', 'disabled');
      this.standardSubmitButton.setAttribute('disabled', 'disabled');

      if (text) {
        this.egiftSubmitButtonText.textContent = text;
        this.standardSubmitButtonText.textContent = text;
      }
    } else {
      this.egiftSubmitButton.removeAttribute('disabled');
      this.standardSubmitButton.removeAttribute('disabled');

      this.egiftSubmitButtonText.textContent = window.variantStrings.addToCart;
      this.standardSubmitButtonText.textContent = window.variantStrings.addToCart;
    }
  }

  validateRecipientAge(age) {
    return new Promise((resolve) => {
      if (!age || isNaN(age)) {
        const modal = document.createElement('div');
        modal.classList.add('age-validation-modal');
        modal.innerHTML = `
              <div class="modal-content">
                <h3 class="modal__heading">We noticed you didn't add an age to your board:</h3>
                <button class="modal__button button button--primary" id="goBackButton">GO BACK & ADD AGE</button>
                <button class="modal__button button button--secondary" id="skipAddToCartButton">SKIP & ADD TO CART</button>
              </div>
            `;

        document.body.appendChild(modal);
        document.body.classList.add('overflow-hidden');

        modal.querySelector('#skipAddToCartButton').addEventListener('click', () => {
          modal.remove();
          resolve('skip');
        });

        modal.querySelector('#goBackButton').addEventListener('click', () => {
          document.body.classList.remove('overflow-hidden');
          modal.remove();
          resolve('cancel');
        });
      } else {
        resolve('valid');
      }
    });
  }

  get variantIdInput() {
    return this.form.querySelector('[name=id]');
  }
}

defineElement('product-form', ProductForm);

class GiftCardForm extends HTMLElement {
  constructor() {
    super();

    this.form = this.querySelector('form');
    this.variantIdInput.disabled = false;
    this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
    this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    this.submitButton = this.querySelector('[type="submit"]');
    this.submitButtonText = this.submitButton.querySelector('span');

    if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

    this.hideErrors = this.dataset.hideErrors === 'true';
  }

  onSubmitHandler(evt) {
    evt.preventDefault();
    if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

    this.handleErrorMessage();

    this.submitButton.setAttribute('aria-disabled', true);
    this.submitButton.classList.add('loading');
    this.querySelector('.loading__spinner').classList.remove('hidden');

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData(this.form);
    if (this.cart) {
      formData.append(
        'sections',
        getCartSectionsToRender().map((section) => section.section)
      );
      formData.append('sections_url', window.location.pathname);
      this.cart.setActiveElement(document.activeElement);
    }
    config.body = formData;

    fetch(`${routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((response) => {
        // Dispatch saved cart item event for customer accounts
        const gwCustomerAccounts = document.querySelector('gw-customer-accounts');
        if (gwCustomerAccounts) {
          const event = new CustomEvent('gw:saved-items-from-cart', {
            detail: {
              productId: this.form.querySelector('[name="product-id"]').value,
              variantId: this.form.querySelector('[name="id"]').value,
              target: this.form,
            },
          });

          gwCustomerAccounts.dispatchEvent(event);
        }

        if (response.status) {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'product-form',
            productVariantId: formData.get('id'),
            errors: response.errors || response.description,
            message: response.message,
          });
          this.handleErrorMessage(response.description);

          const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
          if (!soldOutMessage) return;
          this.submitButton.setAttribute('aria-disabled', true);
          this.submitButtonText.classList.add('hidden');
          soldOutMessage.classList.remove('hidden');
          this.error = true;
          return;
        } else if (!this.cart) {
          window.location = window.routes.cart_url;
          return;
        }

        if (!this.error)
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: formData.get('id'),
            cartData: response,
          });

        this.error = false;
        this.cart.renderContents(response);
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        this.submitButton.classList.remove('loading');
        if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
        if (!this.error) this.submitButton.removeAttribute('aria-disabled');
        this.querySelector('.loading__spinner').classList.add('hidden');
      });
  }

  handleErrorMessage(errorMessage = false) {
    if (this.hideErrors) return;

    this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
    if (!this.errorMessageWrapper) return;
    this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

    this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

    if (errorMessage) {
      this.errorMessage.textContent = errorMessage;
    }
  }

  toggleSubmitButton(disable = true, text) {
    if (disable) {
      this.submitButton.setAttribute('disabled', 'disabled');
      if (text) this.submitButtonText.textContent = text;
    } else {
      this.submitButton.removeAttribute('disabled');
      this.submitButtonText.textContent = window.variantStrings.addToCart;
    }
  }

  get variantIdInput() {
    return this.form.querySelector('[name=id]');
  }
}

defineElement('gift-card-form', GiftCardForm);
